export const LANG_COOKIE_NAME = "langCode";
export const DEFAULT_LANGUAGE_CODE = "en";
export const SUPPORTED_LOCALES = ["hindi", "telugu", "tamil"] as const;
export const SUPPORTED_LOCALE_CODES_APP = ["hi", "te", "ta"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
const LANGUAGE_TAG_REGEX = /^[a-z]{2,8}(?:-[a-z0-9]{2,8})*$/;
const LANGUAGE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 7;

const LOCALE_TO_STORAGE_CODE: Record<string, string> = {
  hindi: "hi",
  telugu: "te",
  tamil: "ta",
};

export function isSupportedLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

export function mapLocaleToStorageCode(locale: string): string {
  return LOCALE_TO_STORAGE_CODE[locale.toLowerCase()] || locale;
}

export function resolveLanguageCode(languageCode?: string | null): string {
  const normalizedLanguageCode = languageCode?.trim().toLowerCase();

  if (!normalizedLanguageCode) {
    return DEFAULT_LANGUAGE_CODE;
  }

  if (!LANGUAGE_TAG_REGEX.test(normalizedLanguageCode)) {
    return DEFAULT_LANGUAGE_CODE;
  }

  return normalizedLanguageCode;
}

export function buildLanguageCookieValue(languageCode: string): string {
  const normalizedLanguageCode = resolveLanguageCode(languageCode);
  const secureAttribute =
    typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";

  return `${LANG_COOKIE_NAME}=${encodeURIComponent(normalizedLanguageCode)}; Path=/; Max-Age=${LANGUAGE_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secureAttribute}`;
}

export function validateLocale(locale?: string | null): string | null {
  if (!locale) return null;

  const normalized = locale.trim().toLowerCase();
  if (!normalized) return null;

  return isSupportedLocale(normalized) ? normalized : null;
}

export function validateLocaleForApp(locale?: string | null): string | null {
  if (!locale) return null;

  const normalized = locale.trim().toLowerCase();
  if (!normalized) return null;

  return (SUPPORTED_LOCALE_CODES_APP as readonly string[]).includes(normalized) ? normalized : null;
}

/**
 * Extract language code from URL pathname.
 * Supports web routes (/hindi/...) and app routes (/app-v1/hi/...).
 */
export function getLanguageFromPathname(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0]?.toLowerCase();

  if (!firstSegment) return DEFAULT_LANGUAGE_CODE;

  // Mobile app routes: /app-v1/{hi|te|ta}/...
  if (firstSegment === "app-v1") {
    return validateLocaleForApp(segments[1]) ?? DEFAULT_LANGUAGE_CODE;
  }

  // Web locale names in first segment (hindi, telugu, tamil)
  if (isSupportedLocale(firstSegment)) {
    return mapLocaleToStorageCode(firstSegment);
  }

  return DEFAULT_LANGUAGE_CODE;
}

/**
 * Extract the raw locale segment from URL pathname (not normalized).
 * Returns the locale as it appears in URL for use in URL building.
 * - /hindi/... → "hindi"
 * - /app-v1/hi/... → "hi"
 * - /credit-pulse-report → null
 */
export function getLocaleFromPathname(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0]?.toLowerCase();

  if (!firstSegment) return null;

  // Mobile app routes: /app-v1/{hi|te|ta}/...
  if (firstSegment === "app-v1") {
    return validateLocaleForApp(segments[1]);
  }

  // Web locale names (hindi, telugu, tamil)
  if (isSupportedLocale(firstSegment)) {
    return firstSegment;
  }

  return null;
}
